import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Stack,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SchoolIcon from '@mui/icons-material/School';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CalendarIcon from '@mui/icons-material/CalendarToday';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useUserContext } from '../../hooks/useUserContext';
import billingService, { type UserSubscriptionStatus, type Subscription } from '../../services/billingService';
import { getApiErrorInfo } from '../../services/api';
import CpaxLogo from '../../components/CpaxLogo';

// Reuse the type scale from IndividualBillingManagement
const TYPE = {
  display:    { fontSize: '1.75rem', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2 },
  subheading: { fontSize: '1.125rem', fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.3 },
  bodyStrong: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.5 },
  body:       { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.5 },
  caption:    { fontSize: '0.75rem',  fontWeight: 500, lineHeight: 1.4 },
  overline:   { fontSize: '0.75rem',  fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' as const, lineHeight: 1.4 },
} as const;

const ADDON_DISPLAY: Record<string, { label: string; icon: typeof SchoolIcon }> = {
  'OnDemand-Module-Yearly': { label: 'On-Demand Courses', icon: SchoolIcon },
  'Webinars-Module-Yearly': { label: 'Live Webinars', icon: VideoLibraryIcon },
  'Enterprise-OnDemand-Module-Yearly': { label: 'On-Demand Courses', icon: SchoolIcon },
  'Enterprise-Webinar-Module-Yearly': { label: 'Live Webinars', icon: VideoLibraryIcon },
};

function formatAddonLabel(id: string): string {
  return ADDON_DISPLAY[id]?.label ?? id.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function getAddonIcon(id: string) {
  return ADDON_DISPLAY[id]?.icon ?? SchoolIcon;
}

function formatCurrency(amount: number, currency: string = 'USD') {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
}

function formatBillingCycle(billingPeriod: string, billingPeriodUnit?: number): string {
  const p = billingPeriod.toLowerCase();
  if (p === 'year' || p === 'yearly' || p === 'annual') return 'year';
  if (p === 'month' || p === 'monthly') return 'month';
  if (billingPeriodUnit === 1 && (p === '1' || p === '12')) return 'year';
  if (p === '1') return 'year';
  return billingPeriod;
}

type PageState = 'loading' | 'success' | 'cancelled' | 'error' | 'processing';

/** Pick the subscription to display — user subscription first, then tenant */
function resolveSubscription(status: UserSubscriptionStatus | null): Subscription | undefined {
  return status?.userSubscription ?? status?.tenantSubscription;
}

interface NextStep {
  icon: typeof SchoolIcon;
  label: string;
  desc: string;
  path: string;
}

function getNextSteps(isNewSignup: boolean, isTenantAdmin: boolean, isTenantSub: boolean): NextStep[] {
  if (isNewSignup) {
    return [
      { icon: MenuBookIcon, label: 'Browse the course catalog', desc: 'Explore CPE courses across your active modules', path: '/app/courses' },
      { icon: AutoAwesomeIcon, label: 'Try the AI assistant', desc: 'Get personalized course recommendations', path: '/app/assistant' },
    ];
  }
  if (isTenantAdmin || isTenantSub) {
    return [
      { icon: GroupAddIcon, label: 'Invite your team', desc: 'Add members to your firm account', path: '/app/tenant-admin/users' },
      { icon: AssessmentIcon, label: 'View compliance reports', desc: 'Track CPE progress across your organization', path: '/app/tenant-admin/compliance-reports' },
    ];
  }
  return [
    { icon: MenuBookIcon, label: 'Browse courses', desc: 'Explore CPE courses across your active modules', path: '/app/courses' },
    { icon: AutoAwesomeIcon, label: 'Try the AI assistant', desc: 'Get personalized course recommendations', path: '/app/assistant' },
  ];
}

export default function SubscriptionConfirmationPage() {
  const { isLoaded, isSignedIn, isTenantAdmin } = useUserContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const chargebeeState = searchParams.get('state');

  // API-created hosted pages redirect back with ?state=succeeded, but quote
  // acceptance redirects (Accept Quote email link) may arrive without a state
  // param — in that case verify against Chargebee via live sync instead of failing.
  const shouldVerify = chargebeeState === 'succeeded' || chargebeeState === null;

  // Login URL that returns the user here (with Chargebee params intact) after sign-in
  const signInUrl = `/auth/login?redirect_url=${encodeURIComponent(location.pathname + location.search)}`;

  // Compute initial state eagerly from query params (avoids synchronous setState in effects)
  const [isNewSignup] = useState(() => sessionStorage.getItem('checkout_flow') === 'signup');
  const [initialPageState] = useState<PageState>(() => {
    if (shouldVerify) return 'loading';
    if (chargebeeState === 'cancelled') return 'cancelled';
    return 'error';
  });
  const [initialError] = useState<string | null>(() => {
    if (shouldVerify || chargebeeState === 'cancelled') return null;
    return 'Checkout was not completed.';
  });

  const [pageState, setPageState] = useState<PageState>(initialPageState);
  const [status, setStatus] = useState<UserSubscriptionStatus | null>(null);
  const [error, setError] = useState<string | null>(initialError);
  const hasInitialized = useRef(false);
  const retryCount = useRef(0);

  const syncFailureMessage = useCallback((err: unknown): string => {
    const apiError = getApiErrorInfo(err);
    // Note: a 401 here happens while Clerk reports a signed-in session, so
    // bouncing to the login page would loop straight back. Surface it as a
    // retryable error instead.
    if (apiError.status === 401) {
      return 'We could not verify your session. Please try again, or sign out and back in.';
    }
    if (apiError.isNetworkError) {
      return 'Unable to connect to the service. Please try again.';
    }
    return apiError.message || 'Failed to confirm subscription. Please try again.';
  }, []);

  // Sync subscription from Chargebee after successful checkout.
  // Never fires while signed out — the API requires auth and would 401.
  useEffect(() => {
    if (!isLoaded || !isSignedIn || hasInitialized.current || !shouldVerify) return;
    hasInitialized.current = true;

    // Clear the checkout flow flag (already read into state)
    sessionStorage.removeItem('checkout_flow');

    const doSync = async () => {
      try {
        const result = await billingService.syncUserSubscription();

        if (result.hasActiveSubscription && resolveSubscription(result)) {
          setStatus(result);
          setPageState('success');
          // Silent re-sync after 3 seconds to pick up webhook propagation
          setTimeout(async () => {
            try {
              const refreshed = await billingService.syncUserSubscription();
              if (refreshed.hasActiveSubscription && resolveSubscription(refreshed)) {
                setStatus(refreshed);
              }
            } catch {
              // Silently ignore — user already sees data
            }
          }, 3000);
        } else {
          // Subscription not yet visible — retry up to 3 times. Quote-acceptance
          // redirects arrive without a state param, and the subscription_created
          // webhook may not have processed yet, so they get the same retry window.
          retryCount.current += 1;
          if (retryCount.current < 4) {
            setTimeout(doSync, 3000);
          } else if (chargebeeState === 'succeeded') {
            setPageState('processing');
          } else {
            // No explicit success signal and no subscription after retries —
            // the user likely landed here without completing a checkout.
            setError('No checkout information found.');
            setPageState('error');
          }
        }
      } catch (err: unknown) {
        setError(syncFailureMessage(err));
        setPageState('error');
      }
    };

    doSync();
  }, [isLoaded, isSignedIn, chargebeeState, shouldVerify, syncFailureMessage]);

  const handleRetry = async () => {
    setPageState('loading');
    setError(null);
    retryCount.current = 0;
    try {
      const result = await billingService.syncUserSubscription();
      if (result.hasActiveSubscription && resolveSubscription(result)) {
        setStatus(result);
        setPageState('success');
      } else if (chargebeeState !== 'succeeded') {
        setError('No checkout information found.');
        setPageState('error');
      } else {
        setPageState('processing');
      }
    } catch (err: unknown) {
      setError(syncFailureMessage(err));
      setPageState('error');
    }
  };

  const isTenantSubscription = status?.subscriptionSource === 'tenant';

  const handleContinue = () => {
    if (isNewSignup) {
      navigate('/auth/individual-background');
    } else if (isTenantAdmin || isTenantSubscription) {
      navigate('/app/tenant-admin/billing');
    } else {
      navigate('/app/assistant');
    }
  };

  const handleReturnToPlans = () => {
    if (isNewSignup) {
      navigate('/auth/individual-plans');
    } else if (isTenantAdmin || isTenantSubscription) {
      navigate('/app/tenant-admin/billing');
    } else {
      navigate('/app/billing');
    }
  };

  const subscription: Subscription | undefined = resolveSubscription(status);

  // ── Hero Gradient (shared across all states) ──
  const heroGradient = (
    <Box
      sx={{
        background: 'linear-gradient(160deg, #0c200f 0%, #1a4328 55%, #0d3d1c 100%)',
        pt: { xs: 4, md: 5 },
        pb: { xs: 5, md: 7 },
        px: 4,
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Subtle grid overlay */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: 0.06,
          backgroundImage: `
            linear-gradient(rgba(86,151,55,0.5) 1px, transparent 1px),
            linear-gradient(90deg, rgba(86,151,55,0.5) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
          pointerEvents: 'none',
        }}
      />
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        {pageState === 'success' || pageState === 'processing' ? (
          <>
            <CheckCircleIcon
              sx={{
                fontSize: 56,
                color: '#4caf50',
                mb: 2,
                filter: 'drop-shadow(0 2px 8px rgba(76,175,80,0.3))',
              }}
            />
            <Typography
              variant="h3"
              sx={{
                color: 'white',
                fontWeight: 700,
                mb: 1,
                fontSize: { xs: 26, md: 34 },
                letterSpacing: '-0.5px',
              }}
            >
              Thank You for Your Purchase
            </Typography>
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.6)',
                fontSize: 15,
              }}
            >
              Your payment has been confirmed
            </Typography>
          </>
        ) : (
          <>
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.45)',
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: 3,
                textTransform: 'uppercase',
                mb: 2.5,
              }}
            >
              CPAx Academy
            </Typography>
            <Typography
              variant="h3"
              sx={{
                color: 'white',
                fontWeight: 700,
                mb: 1,
                fontSize: { xs: 26, md: 34 },
                letterSpacing: '-0.5px',
              }}
            >
              {pageState === 'loading' ? 'Processing Your Payment' : 'Subscription'}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );

  // ── Signed-out State (public page) ──
  // Security: never assert to an unauthenticated viewer that a specific payment
  // succeeded — query params are attacker-controllable. Use conditional wording
  // and only show verified subscription details after sign-in.
  if (isLoaded && !isSignedIn) {
    // Cancelled checkout never claims a payment happened, so it is safe to
    // show unauthenticated — and prompting these visitors to sign in to view
    // "subscription details" would be misleading.
    const isCancelled = chargebeeState === 'cancelled';
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'surface.subtle' }}>
        <AppBar position="static" elevation={0} sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Toolbar sx={{ px: { xs: 2, sm: 3 } }}>
            <CpaxLogo height={28} showMark={false} />
          </Toolbar>
        </AppBar>
        <Box
          sx={{
            background: 'linear-gradient(160deg, #0c200f 0%, #1a4328 55%, #0d3d1c 100%)',
            pt: { xs: 4, md: 5 },
            pb: { xs: 5, md: 7 },
            px: 4,
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              opacity: 0.06,
              backgroundImage: `
                linear-gradient(rgba(86,151,55,0.5) 1px, transparent 1px),
                linear-gradient(90deg, rgba(86,151,55,0.5) 1px, transparent 1px)
              `,
              backgroundSize: '60px 60px',
              pointerEvents: 'none',
            }}
          />
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.45)',
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: 3,
                textTransform: 'uppercase',
                mb: 2.5,
              }}
            >
              CPAx Academy
            </Typography>
            <Typography
              variant="h3"
              sx={{
                color: 'white',
                fontWeight: 700,
                mb: 1,
                fontSize: { xs: 26, md: 34 },
                letterSpacing: '-0.5px',
              }}
            >
              {isCancelled ? 'Subscription' : 'Sign In to Continue'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ maxWidth: 620, mx: 'auto', px: { xs: 2, sm: 3 }, mt: 4, pb: 8 }}>
          <Box
            sx={{
              borderRadius: 3,
              boxShadow: '0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)',
              p: { xs: 4, sm: 5 },
              bgcolor: 'white',
              textAlign: 'center',
            }}
          >
            {isCancelled ? (
              <>
                <Typography sx={{ ...TYPE.display, color: 'text.primary', mb: 1 }}>
                  Checkout Cancelled
                </Typography>
                <Typography sx={{ ...TYPE.body, color: 'text.secondary', mb: 3, maxWidth: '52ch', mx: 'auto' }}>
                  Your checkout was cancelled and you have not been charged.
                  You can return to your checkout link to try again whenever you're ready.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate(signInUrl)}
                  endIcon={<ArrowForwardIcon />}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Sign In
                </Button>
              </>
            ) : (
              <>
                <Typography sx={{ ...TYPE.display, color: 'text.primary', mb: 1 }}>
                  Almost There
                </Typography>
                <Typography sx={{ ...TYPE.body, color: 'text.secondary', mb: 3, maxWidth: '52ch', mx: 'auto' }}>
                  If you just completed checkout, your subscription is being set up.
                  Sign in to view your subscription details and get started.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate(signInUrl)}
                  endIcon={<ArrowForwardIcon />}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Sign In
                </Button>
                <Typography sx={{ ...TYPE.caption, color: 'text.disabled', mt: 3 }}>
                  Haven't set up your account yet? Check your email for your invitation to CPAx Academy.
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  // ── Loading State ──
  if (pageState === 'loading' || !isLoaded) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'surface.subtle' }}>
        <AppBar position="static" elevation={0} sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Toolbar sx={{ px: { xs: 2, sm: 3 } }}>
            <CpaxLogo height={28} showMark={false} />
          </Toolbar>
        </AppBar>
        {heroGradient}
        <Box sx={{ maxWidth: 620, mx: 'auto', px: { xs: 2, sm: 3 }, mt: 4, pb: 8 }}>
          <Box
            sx={{
              borderRadius: 3,
              boxShadow: '0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)',
              p: { xs: 4, sm: 5 },
              bgcolor: 'white',
              textAlign: 'center',
            }}
          >
            <CircularProgress size={40} sx={{ mb: 2.5 }} />
            <Typography sx={{ ...TYPE.bodyStrong, color: 'text.secondary' }}>
              Confirming your payment...
            </Typography>
            <Typography sx={{ ...TYPE.caption, color: 'text.disabled', mt: 1 }}>
              This only takes a moment
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  // ── Cancelled State ──
  if (pageState === 'cancelled') {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'surface.subtle' }}>
        <AppBar position="static" elevation={0} sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Toolbar sx={{ px: { xs: 2, sm: 3 } }}>
            <CpaxLogo height={28} showMark={false} />
          </Toolbar>
        </AppBar>
        {heroGradient}
        <Box sx={{ maxWidth: 620, mx: 'auto', px: { xs: 2, sm: 3 }, mt: 4, pb: 8 }}>
          <Box
            sx={{
              borderRadius: 3,
              boxShadow: '0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)',
              p: { xs: 4, sm: 5 },
              bgcolor: 'white',
              textAlign: 'center',
            }}
          >
            <Typography sx={{ ...TYPE.display, color: 'text.primary', mb: 1 }}>
              Checkout Cancelled
            </Typography>
            <Typography sx={{ ...TYPE.body, color: 'text.secondary', mb: 3, maxWidth: '50ch', mx: 'auto' }}>
              Your checkout was cancelled and you have not been charged. You can return to the plans page to try again whenever you're ready.
            </Typography>
            <Button
              variant="contained"
              onClick={handleReturnToPlans}
              endIcon={<ArrowForwardIcon />}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Return to Plans
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  // ── Error State ──
  if (pageState === 'error') {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'surface.subtle' }}>
        <AppBar position="static" elevation={0} sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Toolbar sx={{ px: { xs: 2, sm: 3 } }}>
            <CpaxLogo height={28} showMark={false} />
          </Toolbar>
        </AppBar>
        {heroGradient}
        <Box sx={{ maxWidth: 620, mx: 'auto', px: { xs: 2, sm: 3 }, mt: 4, pb: 8 }}>
          <Box
            sx={{
              borderRadius: 3,
              boxShadow: '0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)',
              p: { xs: 4, sm: 5 },
              bgcolor: 'white',
              textAlign: 'center',
            }}
          >
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
              {error}
            </Alert>
            <Stack direction="row" spacing={2} justifyContent="center">
              <Button
                variant="outlined"
                onClick={handleReturnToPlans}
                sx={{ textTransform: 'none' }}
              >
                Return to Plans
              </Button>
              {shouldVerify && (
                <Button
                  variant="contained"
                  onClick={handleRetry}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Retry
                </Button>
              )}
            </Stack>
          </Box>
        </Box>
      </Box>
    );
  }

  // ── Processing State (webhook delay) ──
  if (pageState === 'processing') {
    const steps = getNextSteps(isNewSignup, isTenantAdmin, false);
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'surface.subtle' }}>
        <AppBar position="static" elevation={0} sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Toolbar sx={{ px: { xs: 2, sm: 3 } }}>
            <CpaxLogo height={28} showMark={false} />
          </Toolbar>
        </AppBar>
        {heroGradient}
        <Box sx={{ maxWidth: 620, mx: 'auto', px: { xs: 2, sm: 3 }, mt: 4, pb: isNewSignup ? '180px' : 8 }}>
          <Box
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)',
              bgcolor: 'white',
            }}
          >
            <Box sx={{ px: { xs: 3, sm: 4 }, pt: { xs: 3, sm: 4 }, pb: 3, textAlign: 'center' }}>
              <Typography sx={{ ...TYPE.body, color: 'text.secondary', maxWidth: '50ch', mx: 'auto' }}>
                Your payment was received and your subscription is being set up.
                This can take a few minutes. A confirmation receipt will be sent to your email.
              </Typography>
            </Box>

            <Divider />

            {/* What's Next */}
            <Box sx={{ px: { xs: 3, sm: 4 }, pt: 3, pb: 1 }}>
              <Typography sx={{ ...TYPE.overline, color: 'text.secondary', display: 'block', mb: 1.5 }}>
                What's Next
              </Typography>
              {steps.map((step) => {
                const Icon = step.icon;
                return (
                  <Box
                    key={step.path}
                    onClick={() => navigate(step.path)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      py: 1.5,
                      px: 1.5,
                      mx: -1.5,
                      borderRadius: 2,
                      cursor: 'pointer',
                      transition: 'background-color 150ms ease-out',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Box
                      sx={(theme) => ({
                        width: 36,
                        height: 36,
                        borderRadius: '10px',
                        bgcolor: `${theme.palette.primary.main}14`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      })}
                    >
                      <Icon sx={{ fontSize: 18, color: 'primary.main' }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ ...TYPE.bodyStrong, color: 'text.primary' }}>
                        {step.label}
                      </Typography>
                      <Typography sx={{ ...TYPE.caption, color: 'text.secondary' }}>
                        {step.desc}
                      </Typography>
                    </Box>
                    <ChevronRightIcon sx={{ fontSize: 20, color: 'text.disabled', flexShrink: 0 }} />
                  </Box>
                );
              })}
            </Box>

            {!isNewSignup && (
              <>
                <Divider sx={{ mx: { xs: 3, sm: 4 } }} />
                <Box sx={{ px: { xs: 3, sm: 4 }, py: 2.5 }}>
                  <Stack direction="row" spacing={1.5}>
                    {isTenantAdmin ? (
                      <Button
                        variant="contained"
                        onClick={() => navigate('/app/tenant-admin')}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                      >
                        Go to Dashboard
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="contained"
                          onClick={() => navigate('/app/assistant')}
                          sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                          Go to Dashboard
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => navigate('/app/billing')}
                          sx={{ textTransform: 'none' }}
                        >
                          View Billing
                        </Button>
                      </>
                    )}
                  </Stack>
                </Box>
              </>
            )}
          </Box>
        </Box>

        {isNewSignup && (
          <AppBar
            position="fixed"
            elevation={0}
            sx={{
              top: 'auto',
              bottom: 0,
              bgcolor: 'background.paper',
              borderTop: '1px solid',
              borderColor: 'divider',
              height: 128,
            }}
          >
            <Toolbar sx={{ height: '100%', px: { xs: 2, sm: 3 }, justifyContent: 'center' }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleContinue}
                endIcon={<ArrowForwardIcon />}
                sx={{
                  px: '22px',
                  py: 1,
                  fontSize: '0.9375rem',
                  fontWeight: 500,
                  letterSpacing: '0.46px',
                }}
              >
                Continue Account Setup
              </Button>
            </Toolbar>
          </AppBar>
        )}
      </Box>
    );
  }

  // ── Success State ──
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'surface.subtle', display: 'flex', flexDirection: 'column' }}>
      <AppBar position="static" elevation={0} sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider', zIndex: 1 }}>
        <Toolbar sx={{ px: { xs: 2, sm: 3 } }}>
          <CpaxLogo height={28} showMark={false} />
        </Toolbar>
      </AppBar>

      {heroGradient}

      <Box sx={{ maxWidth: 620, mx: 'auto', px: { xs: 2, sm: 3 }, mt: 4, pb: isNewSignup ? '180px' : 8, width: '100%' }}>
        {/* Subscription Details Card */}
        {subscription && (
          <Box
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)',
              bgcolor: 'white',
            }}
          >
            {/* Plan header */}
            <Box sx={{ px: { xs: 3, sm: 4 }, pt: { xs: 3, sm: 4 }, pb: 3 }}>
              <Typography sx={{ ...TYPE.overline, color: 'primary.main', display: 'block', mb: 0.75 }}>
                Your Plan
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography sx={{ ...TYPE.display, color: 'text.primary' }}>
                  {subscription.planName}
                </Typography>
                <Chip
                  label="Active"
                  color="success"
                  size="small"
                  sx={{ ...TYPE.caption, fontWeight: 600, height: 24 }}
                />
              </Box>
            </Box>

            <Divider />

            {/* Details */}
            <Box sx={{ px: { xs: 3, sm: 4 }, py: 3 }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: subscription.activeAddonIds?.length ? '1fr 1fr' : '1fr' },
                  gap: { xs: 2, sm: 0 },
                }}
              >
                {/* Billing */}
                <Box sx={{ pr: { sm: 3 } }}>
                  <Typography sx={{ ...TYPE.overline, color: 'text.secondary' }}>
                    Billing
                  </Typography>
                  <Typography sx={{ ...TYPE.display, mt: 0.75, fontVariantNumeric: 'tabular-nums' }}>
                    {formatCurrency(subscription.planAmount, subscription.currency)}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <CalendarIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                    <Typography sx={{ ...TYPE.caption, color: 'text.secondary' }}>
                      per {formatBillingCycle(subscription.billingPeriod, subscription.billingPeriodUnit)} &middot; billed annually
                    </Typography>
                  </Box>
                </Box>

                {/* Active modules */}
                {subscription.activeAddonIds?.length > 0 && (
                  <Box
                    sx={{
                      pl: { sm: 3 },
                      borderLeft: { xs: 'none', sm: '1px solid' },
                      borderColor: { sm: 'divider' },
                    }}
                  >
                    <Typography sx={{ ...TYPE.overline, color: 'text.secondary' }}>
                      Active Modules
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.75 }}>
                      {subscription.activeAddonIds.map(id => {
                        const Icon = getAddonIcon(id);
                        const isWebinar = id.toLowerCase().includes('webinar');
                        return (
                          <Chip
                            key={id}
                            icon={<Icon sx={{ fontSize: '16px !important' }} />}
                            label={formatAddonLabel(id)}
                            size="small"
                            sx={(theme) => {
                              const token = theme.palette.status[isWebinar ? 'webinar' : 'onDemand'];
                              const iconColor = isWebinar ? theme.palette.primary.main : theme.palette.success.main;
                              return {
                                ...TYPE.caption,
                                bgcolor: token.bg,
                                color: token.fg,
                                border: 'none',
                                '& .MuiChip-icon': { color: iconColor },
                              };
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>

            <Divider />

            {/* What's Next */}
            <Box sx={{ px: { xs: 3, sm: 4 }, pt: 3, pb: 1 }}>
              <Typography sx={{ ...TYPE.overline, color: 'text.secondary', display: 'block', mb: 1.5 }}>
                What's Next
              </Typography>
              {getNextSteps(isNewSignup, isTenantAdmin, !!isTenantSubscription).map((step) => {
                const Icon = step.icon;
                return (
                  <Box
                    key={step.path}
                    onClick={() => navigate(step.path)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      py: 1.5,
                      px: 1.5,
                      mx: -1.5,
                      borderRadius: 2,
                      cursor: 'pointer',
                      transition: 'background-color 150ms ease-out',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Box
                      sx={(theme) => ({
                        width: 36,
                        height: 36,
                        borderRadius: '10px',
                        bgcolor: `${theme.palette.primary.main}14`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      })}
                    >
                      <Icon sx={{ fontSize: 18, color: 'primary.main' }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ ...TYPE.bodyStrong, color: 'text.primary' }}>
                        {step.label}
                      </Typography>
                      <Typography sx={{ ...TYPE.caption, color: 'text.secondary' }}>
                        {step.desc}
                      </Typography>
                    </Box>
                    <ChevronRightIcon sx={{ fontSize: 20, color: 'text.disabled', flexShrink: 0 }} />
                  </Box>
                );
              })}
            </Box>

            <Divider sx={{ mx: { xs: 3, sm: 4 } }} />

            {/* Receipt note + navigation */}
            <Box sx={{ px: { xs: 3, sm: 4 }, py: 2.5 }}>
              <Typography sx={{ ...TYPE.caption, color: 'text.disabled' }}>
                A confirmation receipt will be sent to your email.
              </Typography>
              {!isNewSignup && (
                <Stack direction="row" spacing={1.5} sx={{ mt: 2.5 }}>
                  {isTenantAdmin || isTenantSubscription ? (
                    <Button
                      variant="contained"
                      onClick={() => navigate('/app/tenant-admin')}
                      sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                      Go to Dashboard
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="contained"
                        onClick={handleContinue}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                      >
                        Go to Dashboard
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => navigate('/app/billing')}
                        sx={{ textTransform: 'none' }}
                      >
                        View Billing
                      </Button>
                    </>
                  )}
                </Stack>
              )}
            </Box>
          </Box>
        )}
      </Box>

      {/* Fixed bottom CTA bar — only for new signups continuing account setup */}
      {isNewSignup && (
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            top: 'auto',
            bottom: 0,
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            height: 128,
          }}
        >
          <Toolbar
            sx={{ height: '100%', px: { xs: 2, sm: 3 }, justifyContent: 'center' }}
          >
            <Button
              variant="contained"
              size="large"
              onClick={handleContinue}
              endIcon={<ArrowForwardIcon />}
              sx={{
                px: '22px',
                py: 1,
                fontSize: '0.9375rem',
                fontWeight: 500,
                letterSpacing: '0.46px',
              }}
            >
              Continue Account Setup
            </Button>
          </Toolbar>
        </AppBar>
      )}
    </Box>
  );
}
